/* Favorites context for managing a wishlist of products across the app.
   Uses localStorage so favorites persist between visits without requiring login. */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'sonner';

export interface FavoriteItem {
  id: string;
  product_variant_id?: string | null;
  product_id?: string | null;
  product_name: string;
  variant_size: string;
  image_url?: string;
  slug?: string;
  unit_price?: number;
}

interface FavoritesContextType {
  favorites: FavoriteItem[];
  favoriteCount: number;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (item: FavoriteItem) => void;
  removeFavorite: (id: string) => void;
  clearFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);
const STORAGE_KEY = 'buitn_favorites';

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setFavorites(JSON.parse(stored));
    } catch {
      // ignore parse errors
    }
  }, []);

  const persist = (items: FavoriteItem[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore storage errors
    }
  };

  const isFavorite = (id: string) => favorites.some((f) => f.id === id);

  const toggleFavorite = (item: FavoriteItem) => {
    setFavorites((prev) => {
      const exists = prev.some((f) => f.id === item.id);
      let next: FavoriteItem[];
      if (exists) {
        next = prev.filter((f) => f.id !== item.id);
        toast.success(`${item.product_name} verwijderd uit favorieten`);
      } else {
        next = [...prev, item];
        toast.success(`${item.product_name} toegevoegd aan favorieten`, {
          icon: item.image_url ? (
            <img src={item.image_url} alt={item.product_name} className="w-10 h-10 object-cover rounded" />
          ) : undefined,
        });
      }
      persist(next);
      return next;
    });
  };

  const removeFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = prev.filter((f) => f.id !== id);
      persist(next);
      return next;
    });
  };

  const clearFavorites = () => {
    setFavorites([]);
    persist([]);
  };

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        favoriteCount: favorites.length,
        isFavorite,
        toggleFavorite,
        removeFavorite,
        clearFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
